import React from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface DiagOdometerProps {
  vin: string;
}

function DiagOdometer({vin}: DiagOdometerProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {getErrorCode} = useApiError();
  const ccid = environmentStore.getState().ccid;
  const {convertDistanceValue, convertDistanceSymbol, revertDistanceValue} = useUnitConverter();
  const {openConfirm} = useCcWebviewUtilsHelper();

  const {data: odometerData, refetch: odometerRefetch} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenancesSestController.getLastOdometer1), vin, ccid],
    queryFn: () => diApi.maintenancesSestController.getLastOdometer1({vin, ccid}),
    select: ({data}) => data,
  });

  const isOdometerDataExist = odometerData.value !== 0 && odometerData.updatedAt !== null;

  const [openUpdateOdometerBottomSheet] = usePendingHandler(
    async () => {
      const newOdometer = await ccWebviewUtils.openBottomSheetWebViewUrl<number>(
        ROUTE_PATHS.BOTTOM_SHEET_DIAG_UPDATE_ODOMETER.getFullPath(),
        {isOdometerDataExist, odometer: odometerData.value, updatedAt: odometerData.updatedAt}
      );

      if (!newOdometer) {
        return;
      }

      emitEventLog({
        event: 'vehicle_distance_update',
        form_step: 'complete',
        click_text: t('T10151'),
        click_location: 'body',
      });
      try {
        if (isOdometerDataExist) {
          await diApi.maintenancesSestController.updateOdometer({
            vin,
            requestBody: {value: revertDistanceValue(newOdometer)},
          });
        } else {
          await diApi.maintenancesSestController.createOdometer({
            vin,
            requestBody: {value: revertDistanceValue(newOdometer)},
          });
        }
        await odometerRefetch();
      } catch (error) {
        const errorCode = await getErrorCode(error);
        if (errorCode === 4000) {
          const isConfirm = await openConfirm({
            title: t('T30155'),
            message: t('T30605_$odometer', {
              odometer: `${formatNumberWithCommas(convertDistanceValue(newOdometer))}${convertDistanceSymbol(newOdometer)}`,
            }),
            okText: t('T30592'),
            cancelText: t('T30593'),
          });
          if (isConfirm) {
            navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY.getFullPath(vin));
            return;
          }
          await openUpdateOdometerBottomSheet();
          return;
        }
        throw error;
      }
    },
    async () => {
      await WebviewUtils.openCustomAlert({
        title: t('T10159'),
        message: t('T10160_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND}),
        buttons: [{text: t('T10114'), style: 'PRIMARY', actionType: 'DEFAULT'}],
      });
    }
  );

  return (
    <section className="section section_driving_distance">
      <div className="section_container_header">
        <h2 className="title_xsmall_b">{t('T10052')}</h2>
      </div>
      <div className="section_container">
        <strong className="driving_distance_title">
          <span className="driving_distance_value">
            {isOdometerDataExist ? formatNumberWithCommas(convertDistanceValue(odometerData.value)) : ERROR.DASH}
          </span>
          <span className="driving_distance_unit">{convertDistanceSymbol(odometerData.value)}</span>
          <span className="ic ic_edit_20px" />
        </strong>
        <p className="driving_distance_desc">{isOdometerDataExist ? t('T10150') : t('T10152')}</p>
        <button
          className="section_container_full_btn"
          type="button"
          onClick={() => {
            emitEventLog({
              event: 'vehicle_distance_update',
              form_step: 'cta click',
              click_text: t('T10151'),
              click_location: 'body',
            });
            openUpdateOdometerBottomSheet();
          }}
        >
          <span className="blind">{t('T10151')}</span>
        </button>
      </div>
    </section>
  );
}

export default DiagOdometer;
